import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import TitleDisplay from '../common-components/TitleDisplay';

interface UnlockFormInputs {
  action: string;
  email: string;
  password: string;
  confirmPassword: string;
  wm_captchaId: string;
  'RegistrationParameters.REQUEST_KEY': string;
}

interface ApiResponse {
  redirectUrl?: string;
  errorMessage?: string;
  errorTitle?: string;
  requestKey?: string;
  j_username?: string;
}

const Unlock: React.FC = () => {
  const { key } = useParams<{ key: string }>();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, touchedFields },
    watch,
  } = useForm<UnlockFormInputs>({
    mode: "onChange"
  });

  const [captchaId, setCaptchaId] = useState('');
  const [requestKey, setRequestKey] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const handleRedirect = (output: ApiResponse) => {
    if (output?.redirectUrl) {
      let redirectUrl = output.redirectUrl;
      if (output?.errorMessage) {
        redirectUrl += `?errorMessage=${encodeURIComponent(output.errorMessage)}`;
      }
      if (output?.errorTitle) {
        redirectUrl += (redirectUrl.includes('?') ? '&' : '?') + `errorTitle=${encodeURIComponent(output.errorTitle)}`;
      }
      window.location.href = redirectUrl;
    }
  };

  useEffect(() => {
    setCaptchaId(uuidv4());

    const fetchData = async () => {
      if (!key) return;

      try {
        const response = await fetch(`${window.location.origin}/login/unlock/${key}`);
        const output = await response.json();

        handleRedirect(output);

        if (output?.requestKey) setRequestKey(output.requestKey);
        if (output?.j_username) setValue("email", output.j_username);
        if (output?.errorMessage) setErrorMessage(output.errorMessage);
      } catch (error) {
        setErrorMessage("Something went wrong while loading form.");
      }
    };

    fetchData();
  }, [key, setValue]);

  const onSubmit = async (data: UnlockFormInputs) => {
    const formData = new URLSearchParams();
    formData.append("action", "unlockAccountSubmit");
    formData.append("j_username", data.email);
    formData.append("j_password", data.password);
    formData.append("cj_password", data.confirmPassword);
    formData.append("RegistrationParameters.REQUEST_KEY", requestKey);
    formData.append("wm_captchaId", captchaId);

    try {
      const response = await fetch(window.location.origin + "/login/unlock", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
        credentials: 'include',
      });

      const outputData = await response.json();
      handleRedirect(outputData);
      setErrorMessage(outputData?.errorMessage || '');
    } catch (error) {
      setErrorMessage("Something went wrong while submitting the form.");
    }
  };

  return (
    <div className="container unlock">
      <div className="inner-wrapper">
        <div className="row">
          <div className="col-md-12">
            <form
              className="form-container unlockAccountForm login col-md-5 col-sm-8"
              onSubmit={handleSubmit(onSubmit)}
            >
              <header className="form-header clearfix">
                <h3 className="heading pull-left">Unlock Account</h3>
                <TitleDisplay unlockHeader="true" linkText="Login" linkTo="/" />
              </header>

              {/* Hidden Inputs */}
              <input type="hidden" value="unlockAccountSubmit" {...register("action")} />
              <input type="hidden" value={captchaId} {...register("wm_captchaId")} />
              <input type="hidden" value={requestKey} {...register("RegistrationParameters.REQUEST_KEY")} />

              {/* Error Message */}
              {errorMessage && (
                <p className="help-block validation alert alert-danger serverError" style={{ display: 'block' }}>
                  {errorMessage}
                </p>
              )}

              <div className="form-row">
                <div className="row"><p>Your account has been locked.</p></div>

                {/* Email (read-only) */}
                <div className="row">
                  <div className="control-group">
                    <label className="control-label username">Email</label>
                    <input
                      type="email"
                      readOnly
                      placeholder="EMAIL"
                      className="inputText"
                      {...register("email")}
                    />
                  </div>
                </div>

                {/* New Password */}
                <div className="row">
                  <div className={`control-group ${errors.password ? 'error' : touchedFields?.password ? 'success' : ''}`}>
                    <label className="control-label">New Password <span className="req">*</span></label>
                    <input
                      type="password"
                      placeholder="NEW PASSWORD"
                      className="small inputText"
                      {...register("password", {
                        required: "Password is required",
                        minLength: { value: 6, message: "Too short: Minimum of '6' characters" },
                      })}
                    />
                    {errors.password && <p className="help-block validation alert alert-danger">{errors.password.message}</p>}
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="row">
                  <div className={`control-group ${errors.confirmPassword ? 'error' : touchedFields?.confirmPassword ? 'success' : ''}`}>
                    <label className="control-label">Confirm Password <span className="req">*</span></label>
                    <input
                      type="password"
                      placeholder="CONFIRM PASSWORD"
                      className="small inputText"
                      {...register("confirmPassword", {
                        required: "Must match the password entered above",
                        validate: value => value === watch("password") || "Passwords do not match"
                      })}
                    />
                    {errors.confirmPassword && <p className="help-block validation alert alert-danger">{errors.confirmPassword.message}</p>}
                  </div>
                </div>

                {/* Submit */}
                <div className="row">
                  <div>
                    <button type="submit" className="btn btn-submit pull-right btn-large">
                      Unlock Account
                    </button>
                  </div>
                </div>

              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Unlock; 